import React from 'react';
import { Mail, Shield } from 'lucide-react';

interface LogoPreviewProps {
  text?: string;
  isTransparent?: boolean;
  backgroundColor?: string;
  primaryColor?: string;
  textColor?: string;
  size?: number;
  gradient?: boolean;
}

const LogoPreview: React.FC<LogoPreviewProps> = ({ 
  text, 
  isTransparent = false,
  backgroundColor = '#3B82F6',
  primaryColor = '#FFFFFF',
  textColor = '#FFFFFF',
  size = 64,
  gradient = true
}) => {
  const iconSize = size;
  const containerSize = size * 8;

  const gradientId = 'logo-gradient';
  const secondaryColor = gradient ? 
    `hsl(${parseInt(primaryColor.slice(1), 16) + 40}, 70%, 50%)` : 
    primaryColor;

  return (
    <div 
      className="flex flex-col items-center justify-center p-12"
      id="logo-preview"
      style={{
        width: containerSize,
        height: containerSize,
        backgroundColor: isTransparent ? 'transparent' : backgroundColor,
        backgroundImage: isTransparent ? `
          linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
          linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
          linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
        ` : undefined,
        backgroundSize: isTransparent ? '20px 20px' : undefined,
        backgroundPosition: isTransparent ? '0 0, 0 10px, 10px -10px, -10px 0px' : undefined
      }}
    >
      <svg width={containerSize} height={containerSize} viewBox={`0 0 ${containerSize} ${containerSize}`}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: primaryColor }} />
            <stop offset="100%" style={{ stopColor: secondaryColor }} />
          </linearGradient>
        </defs>
        
        <g transform={`translate(${containerSize/2 - iconSize/2}, ${containerSize/2 - iconSize/2})`}>
          {/* Mail Icon */}
          <path
            d={`M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6ZM20 6L12 11L4 6H20ZM20 18H4V8L12 13L20 8V18Z`}
            transform={`scale(${iconSize/24})`}
            fill={gradient ? `url(#${gradientId})` : primaryColor}
          />
          
          {/* Shield Icon (overlay) */}
          <path
            d={`M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1ZM12 11.99H19C18.47 16.11 15.72 19.78 12 20.93V12H5V6.3L12 3.19V11.99Z`}
            transform={`scale(${iconSize/24})`}
            fill={gradient ? `url(#${gradientId})` : primaryColor}
            opacity="0.3"
          />
          
          {/* Decorative Dots */}
          <circle
            cx={iconSize + iconSize/4}
            cy={-iconSize/4}
            r={iconSize/8}
            fill={gradient ? `url(#${gradientId})` : primaryColor}
            opacity="0.8"
          />
          <circle
            cx={-iconSize/4}
            cy={iconSize + iconSize/4}
            r={iconSize/8}
            fill={gradient ? `url(#${gradientId})` : primaryColor}
            opacity="0.6"
          />
        </g>
        
        {text && (
          <text
            x={containerSize/2}
            y={containerSize/2 + iconSize}
            textAnchor="middle"
            style={{
              fontSize: `${size/2}px`,
              fontWeight: 'bold',
              fill: textColor,
              fontFamily: 'Arial, sans-serif'
            }}
          >
            {text}
          </text>
        )}
      </svg>
    </div>
  );
};

export default LogoPreview;